import {
  Control,
  ControlConditions,
  ControlOptions,
  GetPresetSectionOptions
} from "@/types/controls";
import {
  BuilderActions,
  BuilderElement,
  BuilderElementNestingRule,
  BuilderElementAttributes,
  DefaultChildElement,
  ElementDependencyAndCondition,
  BuilderElementAdditionalClasses
} from "@/types/elements";
import { getElementPhpTemplate } from "./php-template-element";
import {
  booleanToString,
  isValidSlug,
  makeValuePhpValid
} from "@/lib/elements/studio/misc";
import { objectGetTypedKeys } from "@/util/type-assertions";
import lodash from "lodash";
import {
  doesControlTypeAcceptChildren,
  getBaseClassNameForBuilderElement
} from "@/lib/elements/utils";
import { getPresetPhpTemplate } from "@/lib/elements/studio/php-template-preset";
import defensiveCopy from "@/util/defensive-copy";

export function elementToPhp(element: BuilderElement, namespace: string) {
  const php = getElementPhpTemplate({
    namespace,
    uiIcon: element.uiIcon ? jsStringToPhp(element.uiIcon) : "SquareIcon",
    phpClassName: getElementPhpClassName(element),
    tag: element.htmlTag.default,
    tagOptions: stringArrayToPhpArray(element.htmlTag.options),
    tagControlPath: element.htmlTag.pathToControl
      ? `"${element.htmlTag.pathToControl}"`
      : "false",
    name: element.name,
    className: getBaseClassNameForBuilderElement(element),
    category: element.category,
    badge: element.badge ? jsObjectToPhpArrayString(element.badge) : "false",
    defaultProperties: element.defaultProperties
      ? jsObjectToPhpArrayString(element.defaultProperties)
      : "false",
    designControls: controlsToPhpArray(element.controls.designSections),
    settingsControls: controlsToPhpArray(element.controls.settingsSections),
    contentControls: controlsToPhpArray(element.controls.contentSections),
    nestingRules: nestingRuleToPhp(element.nestingRule),
    dependencies: dependenciesToPhpArray(element.dependencies),
    actions: actionsToPhpArray(element.actions),
    defaultChildrenElements: element.defaultChildren
      ? defaultChildrenToPhp(element.defaultChildren)
      : "false",
    attributes: attributesToPhp(element.attributes),
    settings: jsObjectToPhpArrayStringButReturnFalseForEmptyArray(
      element.settings
    ),
    addPanelRules: jsObjectToPhpArrayStringButReturnFalseForEmptyArray(
      element.addPanelRules
    ),
    experimental: booleanToString(element.experimental),
    order: element.order.toString(),
    additionalClasses: additionalClassesToPhp(element.additionalClasses),
    dynamicPropertyPaths: element.dynamicPropertyPaths
      ? jsObjectToPhpArrayString(element.dynamicPropertyPaths)
      : "false",
    projectManagement: element.projectManagement
      ? jsObjectToPhpArrayString(element.projectManagement)
      : "false",
    propertyPathsToWhitelistInFlatProps:
      element.propertyPathsToWhitelistInFlatProps &&
      element.propertyPathsToWhitelistInFlatProps.length
        ? stringArrayToPhpArray(element.propertyPathsToWhitelistInFlatProps)
        : "false",
    propertyPathsToSsrElementWhenValueChanges:
      element.propertyPathsToSsrElementWhenValueChanges &&
      element.propertyPathsToSsrElementWhenValueChanges.length
        ? stringArrayToPhpArray(
            element.propertyPathsToSsrElementWhenValueChanges
          )
        : "false",
    spacingBars: element.spacingBars
      ? jsObjectToPhpArrayString(element.spacingBars)
      : "false"
  });

  return {
    php,
    html: element.template,
    css: element.cssTemplate,
    defaultCss: element.defaultCss
  };
}

export function presetToPhp(
  slug: string,
  presetArg: Control,
  namespace: string
) {
  const preset = defensiveCopy(presetArg);
  const options = defensiveCopy(preset.options.sectionOptions?.preset);

  preset.options.sectionOptions = {
    ...preset.options.sectionOptions,
    preset: {
      slug
    }
  };

  if (options) {
    delete options.slug;
  }

  return getPresetPhpTemplate(
    slug,
    controlToPhp(preset),
    namespace,
    options && Object.keys(options).length
      ? jsObjectToPhpArrayString(options)
      : null
  );
}

export function slugToPhpSlug(slug: string) {
  // We need it to be "\\" in PHP so that it becomes "\" in JS
  return slug.replace("\\", "\\\\");
}

/** UTILS **/
export function elementNameToPhpClassName(elementName: string) {
  // Capitalize all first words and remove spaces, "-" and "_"
  // "colorful pricing" => ColorfulPricing
  return elementName
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.substring(1))
    .join(" ")
    .replace(/-*_* */g, "");
}

export function getMacroName(namespace: string, name: string) {
  const itemName = elementNameToPhpClassName(name);

  return `${namespace}_${itemName}`;
}

export function getFinalDirectoryPath({
  directoryPath,
  elementName
}: {
  directoryPath: string;
  elementName: string;
}) {
  return `${directoryPath}/${elementName.replace(/ /g, "_")}`;
}

export function removeElementPathFromDirectoryPath(directoryPath: string) {
  // removes everything after the last "/"
  return directoryPath.replace(/\/[^/]+$/g, "");
}

function stringArrayToPhpArray(stringArray: string[]) {
  return `[${stringArray.map(val => `'${val}'`).join(", ")}]`;
}

export function jsObjectToPhpArrayString<T>(jsObj: T): string {
  if (Object.keys(jsObj).length === 0) {
    if (Array.isArray(jsObj)) {
      return "[]";
    }

    // empty object and empty array are very different in JS.
    // this caused an ugly bug: https://github.com/soflyy/breakdance/pull/2518/files
    return "(object)[]";
  }

  const phpArray = (Object.keys(jsObj) as (keyof T)[])
    .map(key => {
      const value = lodash.isObject(jsObj[key])
        ? jsObjectToPhpArrayString(jsObj[key])
        : makeValuePhpValid(jsObj[key]);

      // don't send undefined values to PHP
      if (value === undefined) {
        return undefined;
      }

      return `'${key}' => ${value}`;
    })
    .filter(v => v !== undefined)
    .join(", ");

  return `[${phpArray}]`;
}

/** **/

function getElementPhpClassName(element: BuilderElement) {
  if (isValidSlug(element.slug)) {
    return element.slug.split("\\")[1];
  }

  return elementNameToPhpClassName(element.name);
}

function nestingRuleToPhp(nestingRule: BuilderElementNestingRule) {
  const restrictedToBeADirectChildOfText =
    nestingRule.restrictedToBeADirectChildOf
      ? `"restrictedToBeADirectChildOf" => ${stringArrayToPhpArray(
          nestingRule.restrictedToBeADirectChildOf
        )},`
      : "";

  const restrictedToBeADescendantOfText =
    nestingRule.restrictedToBeADescendantOf
      ? `"restrictedToBeADescendantOf" => ${stringArrayToPhpArray(
          nestingRule.restrictedToBeADescendantOf
        )},`
      : "";

  const notAllowedWhenNodeTypeIsPresentInTreeText =
    nestingRule.notAllowedWhenNodeTypeIsPresentInTree
      ? `"notAllowedWhenNodeTypeIsPresentInTree" => ${stringArrayToPhpArray(
          nestingRule.notAllowedWhenNodeTypeIsPresentInTree
        )},`
      : "";

  return `["type" => "${nestingRule.type}", ${restrictedToBeADirectChildOfText} ${restrictedToBeADescendantOfText} ${notAllowedWhenNodeTypeIsPresentInTreeText}]`;
}

function controlsToPhpArray(controls: Control[]) {
  return `[${controls.map(controlOrPresetToPhp).join(", ")}]`;
}

function controlChildrenToPhpArray(control: Control) {
  if (doesControlTypeAcceptChildren(control.options.type)) {
    return controlsToPhpArray(control.children);
  }

  return "[]";
}

function getControlOptionsWithoutExternalConditions(
  options: ControlOptions
): ControlOptions {
  if (!options.condition) return options;

  if (Array.isArray(options.condition)) {
    options.condition = options.condition
      .map(conditions => conditions.filter(condition => !condition.external))
      .filter(conditions => conditions.length);

    if (options.condition.length === 0) {
      delete options.condition;
    }
  } else if (options.condition.external) {
    delete options.condition;
  }

  return options;
}

function controlOrPresetToPhp(controlArg: Control): string {
  const control = defensiveCopy(controlArg);

  control.options = getControlOptionsWithoutExternalConditions(control.options);

  if (typeof control.options.sectionOptions?.preset?.slug === "string") {
    control.options = {
      ...control.options,
      sectionOptions: {
        ...control.options.sectionOptions,
        preset: {
          ...control.options.sectionOptions.preset,
          slug: slugToPhpSlug(control.options.sectionOptions.preset.slug)
        },
        type: control.options.sectionOptions?.type || "popout"
      }
    };

    const getPresetOptions: GetPresetSectionOptions = {
      condition: control.options.condition,
      type: control.options.sectionOptions?.type
    };

    return `getPresetSection(
      "${control.options.sectionOptions?.preset?.slug}",
      "${control.label}",
      "${control.slug}",
       ${
         Object.keys(getPresetOptions).length
           ? jsObjectToPhpArrayString(getPresetOptions)
           : "false"
       }
     )`;
  } else {
    return controlToPhp(control);
  }
}

function controlToPhp(control: Control): string {
  return `c(
        "${control.slug}",
        "${control.label}",
        ${controlChildrenToPhpArray(control)},
        ${jsObjectToPhpArrayString(control.options)},
        ${booleanToString(control.enableMediaQueries)},
        ${booleanToString(control.enableHover)},
        ${stringArrayToPhpArray(control.keywords ?? [])},
      )`;
}

function dependenciesToPhpArray(
  dependencies: ElementDependencyAndCondition[] | false
) {
  if (!dependencies) {
    return "false";
  }

  let allDependencies = "";

  dependencies.forEach((dependency, index) => {
    let dependenciesAsPhpArray = "";
    objectGetTypedKeys(dependency).forEach(key => {
      const dependencyAsString =
        typeof dependency[key] === "string"
          ? jsStringToPhp(dependency[key] as string)
          : (dependency[key] as string[])
              ?.map(dependency => jsStringToPhp(dependency))
              .join("','");

      if (!dependencyAsString) {
        return;
      } else if (
        key === "builderCondition" ||
        key === "frontendCondition" ||
        key === "title"
      ) {
        dependenciesAsPhpArray += `'${key}' => '${dependencyAsString}',`;
      } else {
        dependenciesAsPhpArray += `'${key}' => ['${dependencyAsString}'],`;
      }
    });

    if (dependenciesAsPhpArray) {
      allDependencies += `'${index}' =>  [${dependenciesAsPhpArray}],`;
    }
  });

  if (!allDependencies.length) {
    return "false";
  }

  return `[${allDependencies}]`;
}

function actionsToPhpArray(actions: BuilderActions | false) {
  if (!actions) {
    return "false";
  }

  let phpArray = "";

  objectGetTypedKeys(actions).forEach(key => {
    if (actions[key]?.length) {
      const actionAsString = actions[key]
        ?.map(action => {
          const dependencyAsString =
            action.dependencies?.filter(Boolean).join("','") || "";

          // return false if empty so we can clean it up from the list
          if (!action.script && !dependencyAsString) {
            return false;
          }

          const safeScript = jsStringToPhp(action.script);
          const actionDependencies = dependencyAsString
            ? `'dependencies' => ['${dependencyAsString}'],`
            : "";
          const runForAllChildren = action.runForAllChildren
            ? `'runForAllChildren' => ${!!action.runForAllChildren},`
            : "";

          return `['script' => '${safeScript}',${actionDependencies}${runForAllChildren}\n],`;
        })
        .filter(Boolean)
        .join("");

      phpArray += `\n\n'${key}' => [${actionAsString}],`;
    }
  });

  if (!phpArray.length) {
    return "false";
  }

  return `[${phpArray}]`;
}

function attributesToPhp(attributes: BuilderElementAttributes | false) {
  if (!attributes) {
    return "false";
  }

  const attributesString = attributes
    .map(attribute => jsObjectToPhpArrayString(attribute))
    .join(", ");

  if (!attributesString.length) {
    return "false";
  }

  return `[${attributesString}]`;
}

function additionalClassesToPhp(
  additionalClasses: BuilderElementAdditionalClasses | false
) {
  if (!additionalClasses) {
    return "false";
  }

  const classesString = additionalClasses
    .map(classInfo => jsObjectToPhpArrayString(classInfo))
    .join(", ");

  if (!classesString.length) {
    return "false";
  }

  return `[${classesString}]`;
}

// NOTE: is there some case where escaping the ' could break someone's code???
// Escape single quotes `'` for code saved in PHP as a string. It'll cause a syntax error otherwise
function jsStringToPhp(code: string) {
  return code.replace(/'/g, "\\'");
}

function jsObjectToPhpArrayStringButReturnFalseForEmptyArray<T>(jsObj: T) {
  if (!jsObj) return "false";

  const phpString = jsObjectToPhpArrayString(jsObj);

  // if the element is full of undefined values it'll be turned to [], but we want false
  if (phpString === "[]") return "false";

  return phpString;
}

function defaultChildrenToPhp(children: DefaultChildElement[] | undefined) {
  if (!children) {
    return "false";
  }

  const childrenString = children
    .map(child => jsObjectToPhpArrayString(child))
    .join(", ");

  if (!childrenString.length) {
    return "false";
  }

  return `[${childrenString}]`;
}

// This doesn't include ALL reserved words, only the ones common in English that could be used by a user
export const phpReservedWords = [
  "new",
  "case",
  "else",
  "const",
  "if",
  "for",
  "namespace",
  "protected",
  "continue",
  "use",
  "public",
  "implements",
  "static",
  "extends",
  "default",
  "class",
  "clone",
  "break",
  "do",
  "final",
  "private",
  "try",
  "or"
];
